import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Link as LinkIcon,
  Groups as GroupsIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import axiosInstance from '../config/axios';

// Function to get target audience label
const getTargetAudienceLabel = (targetAudience) => {
  const targetAudiences = {
    'kinder_jugendliche': 'Kinder & Jugendliche',
    'bevoelkerung': 'Bevölkerung',
    'unternehmen': 'Unternehmen',
    'behoerden': 'Behörden',
    'militaer': 'Militär',
    'keine_angabe': 'Keine Angabe'
  };
  return targetAudiences[targetAudience] || 'Bevölkerung';
};

const EventSuggestionsManagement = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchSuggestions();
  }, [statusFilter]);

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await axiosInstance.get('/event-suggestions', { params });
      console.log('Fetched suggestions:', response.data);
      response.data.forEach(suggestion => {
        if (suggestion.organizer_logo) {
          console.log(`Suggestion "${suggestion.title}" has logo: ${suggestion.organizer_logo}`);
        }
      });
      setSuggestions(response.data);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError('Fehler beim Laden der Veranstaltungsvorschläge');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSuggestion = (suggestion) => {
    console.log('Viewing suggestion:', suggestion);
    console.log('Organizer logo ID:', suggestion.organizer_logo);
    console.log('Show logo:', suggestion.show_logo);
    setSelectedSuggestion(suggestion);
    setViewDialogOpen(true);
  };

  const updateSuggestionLogoVisibility = async (suggestionId, showLogo) => {
    try {
      await axiosInstance.put(`/event-suggestions/${suggestionId}`, {
        show_logo: showLogo
      });
      
      // Update local state
      const updatedSuggestions = suggestions.map(s => 
        s.id === suggestionId 
          ? { ...s, show_logo: showLogo }
          : s
      );
      setSuggestions(updatedSuggestions);
      
      // Update selected suggestion if it's the same one
      if (selectedSuggestion && selectedSuggestion.id === suggestionId) {
        setSelectedSuggestion({ ...selectedSuggestion, show_logo: showLogo });
      }
      
      setSuccess(`Logo-Sichtbarkeit für "${suggestions.find(s => s.id === suggestionId)?.title}" aktualisiert`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating logo visibility:', err);
      setError('Fehler beim Aktualisieren der Logo-Sichtbarkeit');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleReviewSuggestion = (suggestion) => {
    setSelectedSuggestion(suggestion);
    setAdminNotes('');
    setReviewDialogOpen(true);
  };

  const handleApproveSuggestion = async () => {
    if (!selectedSuggestion) return;

    try {
      await axiosInstance.put(`/event-suggestions/${selectedSuggestion.id}`, {
        status: 'approved',
        admin_notes: adminNotes || null,
      });
      
      setSuccess('Veranstaltungsvorschlag wurde genehmigt und zum Kalender hinzugefügt');
      setReviewDialogOpen(false);
      fetchSuggestions();
    } catch (err) {
      console.error('Error approving suggestion:', err);
      setError('Fehler beim Genehmigen des Vorschlags');
    }
  };

  const handleRejectSuggestion = async () => {
    if (!selectedSuggestion) return;

    try {
      await axiosInstance.put(`/event-suggestions/${selectedSuggestion.id}`, {
        status: 'rejected',
        admin_notes: adminNotes || null,
      });
      
      setSuccess('Veranstaltungsvorschlag wurde abgelehnt');
      setReviewDialogOpen(false);
      fetchSuggestions();
    } catch (err) {
      console.error('Error rejecting suggestion:', err);
      setError('Fehler beim Ablehnen des Vorschlags');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Ausstehend';
      case 'approved': return 'Genehmigt';
      case 'rejected': return 'Abgelehnt';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const pendingCount = suggestions.filter(s => s.status === 'pending').length;
  const approvedCount = suggestions.filter(s => s.status === 'approved').length;
  const rejectedCount = suggestions.filter(s => s.status === 'rejected').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <EventIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" component="h1">
          Veranstaltungsvorschläge verwalten
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={fetchSuggestions}
          disabled={loading}
          variant="outlined"
        >
          Aktualisieren
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip label={pendingCount} color="warning" />
                <Typography variant="h6">Ausstehend</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip label={approvedCount} color="success" />
                <Typography variant="h6">Genehmigt</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip label={rejectedCount} color="error" />
                <Typography variant="h6">Abgelehnt</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status filtern</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status filtern"
          >
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="pending">Ausstehend</MenuItem>
            <MenuItem value="approved">Genehmigt</MenuItem>
            <MenuItem value="rejected">Abgelehnt</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Titel</TableCell>
              <TableCell>Eingereicht von</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell>Zielgruppe</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Eingereicht am</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : suggestions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Keine Veranstaltungsvorschläge gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              suggestions.map((suggestion) => (
                <TableRow key={suggestion.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {suggestion.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {suggestion.event_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      <Typography variant="body2">
                        {suggestion.user?.first_name} {suggestion.user?.last_name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {suggestion.user?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon fontSize="small" />
                      <Typography variant="body2">
                        {formatDate(suggestion.start_date)}
                      </Typography>
                    </Box>
                    {suggestion.end_date && (
                      <Typography variant="caption" color="text.secondary">
                        bis {formatDate(suggestion.end_date)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LanguageIcon fontSize="small" />
                      <Typography variant="body2">
                        {getTargetAudienceLabel(suggestion.target_audience)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(suggestion.status)}
                      color={getStatusColor(suggestion.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(suggestion.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Anzeigen">
                        <IconButton
                          size="small"
                          onClick={() => handleViewSuggestion(suggestion)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {suggestion.status === 'pending' && (
                        <>
                          <Tooltip title="Genehmigen">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReviewSuggestion(suggestion)}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ablehnen">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReviewSuggestion(suggestion)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Suggestion Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon color="primary" />
            <Typography variant="h6">
              {selectedSuggestion?.title}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSuggestion && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Beschreibung
                  </Typography>
                  <Typography variant="body1">
                    {selectedSuggestion.description || 'Keine Beschreibung'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Startdatum
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedSuggestion.start_date)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Enddatum
                  </Typography>
                  <Typography variant="body1">
                    {selectedSuggestion.end_date ? formatDate(selectedSuggestion.end_date) : 'Nicht angegeben'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Typ
                  </Typography>
                  <Typography variant="body1">
                    {selectedSuggestion.event_type}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <LanguageIcon fontSize="small" sx={{ mr: 1 }} />
                    Zielgruppe
                  </Typography>
                  <Typography variant="body1">
                    {getTargetAudienceLabel(selectedSuggestion.target_audience)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedSuggestion.status)}
                    color={getStatusColor(selectedSuggestion.status)}
                    size="small"
                  />
                </Grid>
                
                {selectedSuggestion.location && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <LocationIcon fontSize="small" sx={{ mr: 1 }} />
                      Ort
                    </Typography>
                    <Typography variant="body1">
                      {selectedSuggestion.location}
                    </Typography>
                  </Grid>
                )}
                
                {selectedSuggestion.organizer && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <GroupsIcon fontSize="small" sx={{ mr: 1 }} />
                      Veranstalter
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      {selectedSuggestion.organizer_logo && (
                        <img 
                          src={`/api/v1/images/${selectedSuggestion.organizer_logo}/data`}
                          alt={`${selectedSuggestion.organizer} Logo`}
                          style={{ 
                            width: '48px', 
                            height: '48px', 
                            objectFit: 'contain',
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            border: '2px solid rgba(66, 165, 245, 0.5)',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                          }}
                          onLoad={() => {
                            console.log('Logo loaded successfully for suggestion:', selectedSuggestion.title);
                          }}
                          onError={(e) => {
                            console.error('Failed to load logo for suggestion:', selectedSuggestion.title, 'Image ID:', selectedSuggestion.organizer_logo);
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <Typography variant="body1">
                        {selectedSuggestion.organizer}
                      </Typography>
                    </Box>
                    
                    {selectedSuggestion.organizer_logo && (
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedSuggestion.show_logo === true}
                              onChange={(e) => {
                                updateSuggestionLogoVisibility(selectedSuggestion.id, e.target.checked);
                              }}
                              color="primary"
                            />
                          }
                          label="Logo in der Veranstaltungsübersicht anzeigen"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {selectedSuggestion.show_logo === true ? 'Logo wird angezeigt' : 'Logo wird nicht angezeigt'}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                )}
                
                {selectedSuggestion.url && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <LinkIcon fontSize="small" sx={{ mr: 1 }} />
                      URL
                    </Typography>
                    <Typography variant="body1">
                      <a href={selectedSuggestion.url} target="_blank" rel="noopener noreferrer">
                        {selectedSuggestion.url}
                      </a>
                    </Typography>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Begründung des Vorschlags
                  </Typography>
                  <Typography variant="body1">
                    {selectedSuggestion.reason}
                  </Typography>
                </Grid>
                
                {selectedSuggestion.admin_notes && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Admin-Notizen
                    </Typography>
                    <Typography variant="body1">
                      {selectedSuggestion.admin_notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Suggestion Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Veranstaltungsvorschlag bewerten
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {selectedSuggestion?.title}
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Admin-Notizen (optional)"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Geben Sie hier Ihre Bewertung oder Notizen ein..."
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleRejectSuggestion}
            color="error"
            variant="outlined"
            startIcon={<CancelIcon />}
          >
            Ablehnen
          </Button>
          <Button
            onClick={handleApproveSuggestion}
            color="success"
            variant="contained"
            startIcon={<CheckCircleIcon />}
          >
            Genehmigen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventSuggestionsManagement;
